import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition, isMobileBreakpoint } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * Fetched page content, parsed once at fetch time so DOMParser never runs on the
   * append critical path. Cards are cloned out of these documents, so a page can be
   * re-inserted any number of times. In-memory only.
   * @type {Map<number, Document>}
   */
  pages = new Map();

  /**
   * Page numbers currently in the DOM.
   * @type {Set<number>}
   */
  #renderedPages = new Set();

  /**
   * In-flight section fetches, keyed by page number.
   * @type {Map<number, Promise<void>>}
   */
  #pageFetches = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {IntersectionObserver | undefined} */
  #prefetchObserver;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isRenderingNext = false;

  /** @type {boolean} */
  #isRenderingPrevious = false;

  /** @type {boolean} */
  #isMutating = false;

  /** @type {boolean} */
  #previousIntersecting = false;

  /** @type {boolean} */
  #nextIntersecting = false;

  /**
   * On ?page=N landing only: ignore previous until the user scrolls down once.
   * Prevents cascading N-1..1 on first paint. NOT used after normal previous renders
   * (that stuck users on page 2 when scrolling up).
   * @type {boolean}
   */
  #requireScrollDownBeforePrevious = false;

  /**
   * Short pause after a previous prepend / top prune to avoid 1↔2 ping-pong.
   * @type {number}
   */
  #previousCooldownUntil = 0;

  /**
   * Keep a small window in the live DOM. Too many pages + scroll compensation
   * thrashes sticky header on iOS; detached cache covers up/down without reparse.
   */
  #maxDomPages = 4;

  /**
   * Pruned pages kept as live nodes (decoded images). Restoring these avoids
   * DOMParser + new <img> (re-download / white flash).
   * @type {Map<number, DocumentFragment>}
   */
  #detachedPages = new Map();

  /**
   * Soft cap on detached node caches (the parsed page document remains as a fallback).
   * Every detached page holds ~24 cards with decoded bitmaps: at 40 pages that is ~960
   * cards of image memory off-DOM, enough for iOS Safari to start discarding rasterized
   * tiles (white repaints) or reload the tab outright.
   */
  #maxDetachedPages = 4;

  /**
   * Pages whose first images have already been pushed into the HTTP cache.
   * @type {Set<number>}
   */
  #warmedPages = new Set();

  /** @type {number} */
  #prefetchRaf = 0;

  /** @type {number} */
  #urlSyncTimeout = 0;

  /** @type {number} */
  #fetchGeneration = 0;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {'up' | 'down' | 'none'} */
  #scrollDirection = 'none';

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#rebuildRenderedPagesFromDOM();
    this.#snapshotDomPagesToCache();
    this.#lastScrollY = window.scrollY;

    const minPage = this.#minRenderedPage();
    // Only gate previous when landing mid-stream (?page=N).
    this.#requireScrollDownBeforePrevious = minPage != null && minPage > 1;
    this.#previousCooldownUntil = 0;

    this.#prefetchAround();
    this.#observeViewMore();

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();
    this.#prefetchObserver?.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('scroll', this.#onScroll);
    clearTimeout(this.#urlSyncTimeout);
    if (this.#prefetchRaf) cancelAnimationFrame(this.#prefetchRaf);
    this.#detachedPages.clear();
  }

  #onScroll = () => {
    const y = window.scrollY;

    // Programmatic scrollTo during prepend/prune must not flip direction to
    // 'down' — that blocked further previous restores (stuck on page 2).
    if (this.#isMutating) {
      this.#lastScrollY = y;
      return;
    }

    const delta = y - this.#lastScrollY;
    if (Math.abs(delta) > 2) {
      this.#scrollDirection = delta > 0 ? 'down' : 'up';
    }
    this.#lastScrollY = y;

    if (this.#requireScrollDownBeforePrevious && y > 80 && this.#scrollDirection === 'down') {
      this.#requireScrollDownBeforePrevious = false;
    }

    // The URL has no visual effect — syncing it per frame meant reading page geometry
    // on every animation frame while scrolling. Defer it to scroll idle instead.
    this.#scheduleUrlSync();

    if (this.#prefetchRaf) return;
    this.#prefetchRaf = requestAnimationFrame(() => {
      this.#prefetchRaf = 0;
      if (this.#scrollDirection === 'down') this.#prefetchAround();
    });
  };

  #scheduleUrlSync() {
    clearTimeout(this.#urlSyncTimeout);
    this.#urlSyncTimeout = window.setTimeout(() => {
      this.#urlSyncTimeout = 0;
      this.#syncUrlFromViewport();
    }, 200);
  }

  /**
   * @returns {boolean}
   */
  #canRenderPrevious() {
    if (this.#requireScrollDownBeforePrevious) return false;

    const previousPage = this.#getPage('previous');
    const hasDetached = Boolean(previousPage && this.#detachedPages.has(previousPage.page));

    // Cooldown avoids 1↔2 ping-pong; still allow near-top restore of live nodes.
    if (Date.now() < this.#previousCooldownUntil) {
      if (!(hasDetached && this.#isNearRenderedTop())) return false;
    }

    // Near the top of the rendered grid, always allow earlier pages even if a
    // programmatic scroll briefly reported direction 'down'.
    if (this.#isNearRenderedTop()) return true;
    if (this.#scrollDirection === 'down') return false;
    return true;
  }

  /**
   * @returns {boolean}
   */
  #isNearRenderedTop() {
    const { grid } = this.refs;
    const first = grid?.querySelector(':scope > [ref="cards[]"]');
    if (!first) return false;
    const top = first.getBoundingClientRect().top;
    return top >= -40 && top < window.innerHeight * 0.75;
  }

  /**
   * @param {number} [ms]
   */
  #armPreviousCooldown(ms = 450) {
    this.#previousCooldownUntil = Date.now() + ms;
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    this.infinityScrollObserver?.disconnect();
    this.#prefetchObserver?.disconnect();

    // Prefetch early (warm JS cache) without appending to the DOM.
    this.#prefetchObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#prefetchAround();
        }
      },
      { rootMargin: '150% 0px' }
    );

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (this.#isMutating) return;
        if (viewTransition.current) await viewTransition.current;
        if (this.#isMutating) return;

        const { viewMorePrevious, viewMoreNext } = this.refs;

        for (const entry of entries) {
          if (entry.target === viewMorePrevious) this.#previousIntersecting = entry.isIntersecting;
          if (entry.target === viewMoreNext) this.#nextIntersecting = entry.isIntersecting;
        }

        if (this.#nextIntersecting || this.#previousIntersecting) {
          this.#prefetchAround();
        }

        // Both sentinels: only act on clear scroll intent (avoids desktop 1↔2).
        if (this.#previousIntersecting && this.#nextIntersecting) {
          if (this.#scrollDirection === 'up' && this.#canRenderPrevious()) {
            await this.#renderPreviousPage();
          } else if (this.#scrollDirection === 'down') {
            await this.#renderNextPage();
          }
          return;
        }

        // Append only while scrolling down — prefetch already warmed the HTML.
        if (this.#nextIntersecting && this.#scrollDirection === 'down') {
          await this.#renderNextPage();
          return;
        }

        if (this.#previousIntersecting && this.#canRenderPrevious()) {
          await this.#renderPreviousPage();
        }
      },
      // Insert before the user reaches the edge — narrow margins left a blank gap
      // while DOMParser + CE upgrade + image hydrate caught up.
      { rootMargin: '90% 0px' }
    );

    if (viewMorePrevious) {
      this.#prefetchObserver.observe(viewMorePrevious);
      this.infinityScrollObserver.observe(viewMorePrevious);
    }
    if (viewMoreNext) {
      this.#prefetchObserver.observe(viewMoreNext);
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {number} pageNumber
   * @returns {boolean}
   */
  #isPageWithinBounds(pageNumber) {
    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage) return false;
    return pageNumber >= 1 && pageNumber <= Number(lastPage);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    return this.#isPageWithinBounds(pageInfo.page);
  }

  #prefetchAround() {
    const pages = this.#renderedPageNumbers();
    if (!pages.length) return;

    const min = pages[0];
    const max = pages[pages.length - 1];

    // Warm neighbours in JS memory only (no cookies / sessionStorage). The immediate
    // neighbours also warm their images, so an append paints from cache.
    this.#fetchSpecificPage(max + 1, undefined, true);
    this.#fetchSpecificPage(min - 1, undefined, true);

    // Each fetch is the whole main-collection section (filters + 24 cards). On mobile
    // the distance-2 pages only competed with image bandwidth on the pages being shown.
    if (isMobileBreakpoint()) return;

    this.#fetchSpecificPage(max + 2);
    if (this.#scrollDirection === 'up') {
      this.#fetchSpecificPage(min - 2);
    }
  }

  /**
   * Start downloading the first gallery image of each card in a fetched page.
   *
   * Nodes parsed by DOMParser live in a document with no browsing context, so setting
   * src/srcset there never starts a request — the download only began once the card was
   * inserted into the live DOM, which is exactly when the user is looking at it (blank
   * cards). Detached images created by the *live* document do load, so this puts the
   * bytes in the HTTP cache before the append.
   * @param {number} pageNumber
   */
  #warmPageImages(pageNumber) {
    if (this.#warmedPages.has(pageNumber)) return;
    this.#warmedPages.add(pageNumber);

    for (const card of this.#cardTemplatesForPage(pageNumber)) {
      const slide = card.querySelector('slideshow-slide');
      if (!slide) continue;

      const image = slide.querySelector('img.product-media__image');
      const holder = slide.querySelector('.product-media[data-deferred-src]');

      const src = image?.getAttribute('src') ?? holder?.getAttribute('data-deferred-src');
      if (!src) continue;

      const srcset = image?.getAttribute('srcset') ?? holder?.getAttribute('data-deferred-srcset');
      const sizes = image?.getAttribute('sizes') ?? holder?.getAttribute('data-sizes');

      const warm = new Image();
      warm.decoding = 'async';
      // `sizes: auto` resolves against a layout box the detached image does not have,
      // which would make it pick a different (uncached) candidate than the real <img>.
      // The theme always appends the real media-query list after it, so use that part.
      const resolvableSizes = sizes?.replace(/^auto\s*,\s*/, '');
      if (resolvableSizes) warm.sizes = resolvableSizes;
      if (srcset) warm.srcset = srcset;
      warm.src = src;
    }
  }

  /**
   * @param {number} pageNumber
   * @param {URL} [url]
   * @param {boolean} [warmImages] - Also download the first image of each card.
   * @returns {Promise<void>}
   */
  #fetchSpecificPage(pageNumber, url = undefined, warmImages = false) {
    if (!this.#isPageWithinBounds(pageNumber)) return Promise.resolve();

    // Never overwrite a hydrated snapshot / never refetch a page we already hold.
    if (this.#detachedPages.has(pageNumber) || this.pages.has(pageNumber)) {
      if (warmImages) this.#warmPageImages(pageNumber);
      return Promise.resolve();
    }

    const pending = this.#pageFetches.get(pageNumber);
    if (pending) {
      return pending;
    }

    const pageUrl = url ? new URL(url.toString()) : this.#pageUrl(pageNumber);

    const fetchGeneration = this.#fetchGeneration;
    const fetchPromise = (async () => {
      const sectionUrl = new URL(pageUrl.toString());
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, sectionUrl);
      if (fetchGeneration !== this.#fetchGeneration) return;
      // Parse here, off the append critical path.
      this.pages.set(pageNumber, this.#parsePageHTML(pageContent));
      if (warmImages) this.#warmPageImages(pageNumber);
    })()
      .catch(() => {
        this.pages.delete(pageNumber);
      })
      .finally(() => {
        this.#pageFetches.delete(pageNumber);
      });

    this.#pageFetches.set(pageNumber, fetchPromise);
    return fetchPromise;
  }

  /**
   * @param {number} pageNumber
   * @returns {Promise<boolean>}
   */
  async #ensurePage(pageNumber) {
    if (this.#detachedPages.has(pageNumber) || this.pages.has(pageNumber)) return true;
    await this.#fetchSpecificPage(pageNumber);
    return this.#detachedPages.has(pageNumber) || this.pages.has(pageNumber);
  }

  async #renderNextPage() {
    if (this.#isRenderingNext || this.#isMutating) return;
    this.#isRenderingNext = true;

    const { grid } = this.refs;

    if (!grid) {
      this.#isRenderingNext = false;
      return;
    }

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage) || this.#renderedPages.has(nextPage.page)) {
      this.#isRenderingNext = false;
      return;
    }

    this.#renderedPages.add(nextPage.page);

    const ready = await this.#ensurePage(nextPage.page);
    const nextPageItemElements = ready ? this.#getGridForPage(nextPage.page) : undefined;

    if (!nextPageItemElements?.length) {
      this.#renderedPages.delete(nextPage.page);
      this.#isRenderingNext = false;
      return;
    }

    await this.#withMutation(() => {
      grid.append(...nextPageItemElements);
      this.#aspectRatioHelper?.processNewElements();
      this.#pruneRenderedPages();
    });

    this.#prefetchAround();
    this.#isRenderingNext = false;
  }

  async #renderPreviousPage() {
    if (this.#isRenderingPrevious || this.#isMutating || !this.#canRenderPrevious()) return;
    this.#isRenderingPrevious = true;

    const { grid } = this.refs;

    if (!grid) {
      this.#isRenderingPrevious = false;
      return;
    }

    const previousPage = this.#getPage('previous');

    if (!previousPage || !this.#shouldUsePage(previousPage) || this.#renderedPages.has(previousPage.page)) {
      this.#isRenderingPrevious = false;
      return;
    }

    this.#renderedPages.add(previousPage.page);

    const ready = await this.#ensurePage(previousPage.page);
    const previousPageItemElements = ready ? this.#getGridForPage(previousPage.page) : undefined;

    if (!previousPageItemElements?.length) {
      this.#renderedPages.delete(previousPage.page);
      this.#isRenderingPrevious = false;
      return;
    }

    await this.#withMutation(() => {
      const firstElement = grid.querySelector(':scope > [ref="cards[]"]');
      const scrollTop = window.scrollY;
      const oldTop = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      if (firstElement) {
        const newTop = firstElement.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: scrollTop + (newTop - oldTop),
          behavior: 'instant',
        });
      }

      this.#pruneRenderedPages();
    });

    // Brief cooldown only — never re-lock until sentinel leaves (that blocked page 1).
    this.#armPreviousCooldown();
    // Keep upward intent after programmatic scroll compensation.
    this.#scrollDirection = 'up';
    this.#lastScrollY = window.scrollY;
    this.#prefetchAround();
    this.#isRenderingPrevious = false;

    // Observer may not re-fire if the sentinel stayed intersecting after prepend.
    if (this.#previousIntersecting || this.#isNearRenderedTop()) {
      window.setTimeout(() => {
        if (!this.isConnected || this.#isMutating) return;
        if ((this.#previousIntersecting || this.#isNearRenderedTop()) && this.#canRenderPrevious()) {
          this.#renderPreviousPage();
        }
      }, 500);
    }
  }

  /**
   * @param {() => void} mutate
   */
  async #withMutation(mutate) {
    this.#isMutating = true;
    try {
      mutate();
    } finally {
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#isMutating = false;
            this.#scheduleUrlSync();
            resolve();
          });
        });
      });
    }
  }

  #pruneRenderedPages() {
    const { grid } = this.refs;
    if (!grid) return;

    const visible = this.#visiblePageNumbers();
    const rendered = this.#renderedPageNumbers();
    if (rendered.length <= this.#maxDomPages) return;

    const anchor = this.#dominantVisiblePage() ?? rendered[Math.floor(rendered.length / 2)];
    const visibleSet = new Set(visible);

    const toRemove = [];
    let remaining = rendered.slice();

    while (remaining.length > this.#maxDomPages) {
      const min = remaining[0];
      const max = remaining[remaining.length - 1];
      const dropMin = Math.abs(min - anchor) >= Math.abs(max - anchor);
      const candidate = dropMin ? min : max;

      if (visibleSet.has(candidate)) {
        const other = dropMin ? max : min;
        if (visibleSet.has(other)) break;
        toRemove.push(other);
        remaining = remaining.filter((page) => page !== other);
        continue;
      }

      toRemove.push(candidate);
      remaining = remaining.filter((page) => page !== candidate);
    }

    for (const page of toRemove) {
      this.#removePageFromDom(page);
    }

    // Brief cooldown after dropping a top page so the previous sentinel does not
    // immediately re-append it (1↔2). Does not permanently block page restore.
    if (toRemove.some((page) => page < (anchor ?? Infinity))) {
      this.#armPreviousCooldown();
    }
  }

  /**
   * @param {number} pageNumber
   */
  #removePageFromDom(pageNumber) {
    const { grid } = this.refs;
    if (!grid) return;

    this.#snapshotPageFromDom(pageNumber);

    const cards = [...grid.querySelectorAll(`:scope > [ref="cards[]"][data-page="${pageNumber}"]`)];
    if (!cards.length) {
      this.#renderedPages.delete(pageNumber);
      return;
    }

    const minRemaining = this.#minRenderedPage();
    const removingFromTop = minRemaining != null && pageNumber <= minRemaining;
    const firstKept = removingFromTop
      ? grid.querySelector(`:scope > [ref="cards[]"]:not([data-page="${pageNumber}"])`)
      : null;
    const scrollTop = window.scrollY;
    const oldTop = firstKept ? firstKept.getBoundingClientRect().top + window.scrollY : 0;

    // Keep live nodes (decoded images) for cheap restore — do not destroy.
    const fragment = document.createDocumentFragment();
    for (const card of cards) fragment.append(card);
    this.#detachedPages.set(pageNumber, fragment);
    this.#evictDetachedPages(pageNumber);
    this.#renderedPages.delete(pageNumber);

    if (firstKept) {
      const newTop = firstKept.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: scrollTop + (newTop - oldTop),
        behavior: 'instant',
      });
    }
  }

  /**
   * Drop farthest detached pages when over the soft cap (HTML snapshot remains).
   * @param {number} aroundPage
   */
  #evictDetachedPages(aroundPage) {
    while (this.#detachedPages.size > this.#maxDetachedPages) {
      let farthest;
      let farthestDist = -1;
      for (const page of this.#detachedPages.keys()) {
        const dist = Math.abs(page - aroundPage);
        if (dist > farthestDist) {
          farthest = page;
          farthestDist = dist;
        }
      }
      if (farthest == null) break;
      this.#detachedPages.delete(farthest);
    }
  }

  #snapshotDomPagesToCache() {
    for (const page of this.#renderedPageNumbers()) {
      this.#snapshotPageFromDom(page);
    }
  }

  /**
   * Fallback snapshot for a page we hold no HTML for (the server-rendered first page).
   * Only runs when the cache is empty: #detachedPages keeps the live, already-hydrated
   * nodes for restore, and serializing 24 hydrated cards to outerHTML on every prune
   * was hundreds of KB of synchronous string work in the middle of a scroll.
   * @param {number} pageNumber
   */
  #snapshotPageFromDom(pageNumber) {
    if (this.pages.has(pageNumber)) return;

    const { grid } = this.refs;
    if (!grid) return;

    const cards = [...grid.querySelectorAll(`:scope > [ref="cards[]"][data-page="${pageNumber}"]`)];
    if (!cards.length) return;

    const html = `<div><ul ref="grid">${cards.map((card) => card.outerHTML).join('')}</ul></div>`;
    this.pages.set(pageNumber, this.#parsePageHTML(html));
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    const selector = ':scope > [ref="cards[]"]';
    const targetCard = isPrevious
      ? grid.querySelector(selector)
      : grid.querySelector(`${selector}:last-child`);

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    if (Number.isNaN(currentCardPage)) return;

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    return { page, url: this.#pageUrl(page) };
  }

  /**
   * @param {number} pageNumber
   * @returns {URL}
   */
  #pageUrl(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    url.hash = '';
    return url;
  }

  /**
   * Nodes already hidden on mobile and unused there — drop before insert so
   * infinite-scroll pages stay lighter. Does not mutate the cached HTML string
   * (desktop restore / resize still get a full parse when needed).
   * @param {Element[]} cards
   * @returns {Element[]}
   */
  #stripMobileOnlyNodes(cards) {
    if (!isMobileBreakpoint() || !cards.length) return cards;

    for (const card of cards) {
      card
        .querySelectorAll(
          [
            '.product-size',
            '.color-variants__thumb',
            '.color-variants__name',
            'quick-add-component',
            '.quick-add',
            'slideshow-arrows',
            '.product-grid-view-zoom-out--details',
          ].join(',')
        )
        .forEach((el) => el.remove());
    }

    return cards;
  }

  /**
   * Before live DOM insert: make the first gallery slide paint immediately.
   * Without aria-hidden="false", theme CSS uses content-visibility:hidden and
   * contain-intrinsic-size collapses the gallery → blank white until slideshow
   * connectedCallback runs.
   *
   * This used to build <img> elements here too, "so fetch starts pre-upgrade". It did
   * not: these cards belong to the DOMParser document, which has no browsing context,
   * so nothing was requested until the card was inserted. The first slide now ships as
   * a real <img> from Liquid and its bytes are warmed by #warmPageImages; the remaining
   * slides are hydrated by ProductCard's own observer, off the insert path.
   * @param {Element[]} cards
   * @returns {Element[]}
   */
  #prepareCardsForPaint(cards) {
    for (const card of cards) {
      const slides = card.querySelectorAll('slideshow-slide');
      if (!slides.length) continue;

      slides.forEach((slide, index) => {
        if (!(slide instanceof HTMLElement)) return;
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      });
    }

    return cards;
  }

  /**
   * @param {string} html
   * @returns {Document}
   */
  #parsePageHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /**
   * Pristine card elements for a page, still owned by the parsed document.
   * @param {number} page
   * @returns {Element[]}
   */
  #cardTemplatesForPage(page) {
    const parsedPage = this.pages.get(page);
    const gridElement = parsedPage?.querySelector('[ref="grid"]');
    if (!gridElement) return [];

    return [...gridElement.querySelectorAll(':scope > [ref="cards[]"]')];
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const detached = this.#detachedPages.get(page);
    if (detached) {
      this.#detachedPages.delete(page);
      const liveCards = [...detached.children];
      if (liveCards.length) return liveCards;
    }

    // Clone so the parsed document stays a reusable template — the same page can be
    // re-inserted after its detached nodes have been evicted.
    const cards = this.#cardTemplatesForPage(page).map((card) => card.cloneNode(true));
    if (!cards.length) return;

    return this.#prepareCardsForPaint(this.#stripMobileOnlyNodes(/** @type {Element[]} */ (cards)));
  }

  #rebuildRenderedPagesFromDOM() {
    this.#renderedPages.clear();
    for (const page of this.#renderedPageNumbersFromDom()) {
      this.#renderedPages.add(page);
    }
  }

  /**
   * Page numbers in the DOM, from the bookkeeping Set. #renderedPages is kept in sync
   * by #renderNextPage / #renderPreviousPage / #removePageFromDom, so re-deriving this
   * from the DOM cost a querySelectorAll over every card on a path that runs per frame.
   * Use #renderedPageNumbersFromDom() when the DOM is the source of truth (init / filters).
   * @returns {number[]}
   */
  #renderedPageNumbers() {
    return [...this.#renderedPages].sort((a, b) => a - b);
  }

  /**
   * @returns {number[]}
   */
  #renderedPageNumbersFromDom() {
    const { grid } = this.refs;
    if (!grid) return [];

    const pages = new Set();
    for (const card of grid.querySelectorAll(':scope > [ref="cards[]"]')) {
      const page = Number(card.dataset?.page);
      if (!Number.isNaN(page)) pages.add(page);
    }
    return [...pages].sort((a, b) => a - b);
  }

  /**
   * First and last card of a page, for cheap geometry. Reading two rects per page
   * instead of one per card keeps the scroll path off a full layout flush.
   * @param {number} pageNumber
   * @returns {{ first: Element, last: Element } | undefined}
   */
  #pageBounds(pageNumber) {
    const { grid } = this.refs;
    if (!grid) return;

    const selector = `:scope > [ref="cards[]"][data-page="${pageNumber}"]`;
    const cards = grid.querySelectorAll(selector);
    const first = cards[0];
    const last = cards[cards.length - 1];
    if (!first || !last) return;

    return { first, last };
  }

  /**
   * @returns {number | undefined}
   */
  #minRenderedPage() {
    const pages = this.#renderedPageNumbers();
    return pages[0];
  }

  /**
   * Vertical extent of a rendered page, from its first and last card only. Cards of a
   * page are contiguous in the grid, so the two edges bound the whole block.
   * @param {number} pageNumber
   * @returns {{ top: number, bottom: number } | undefined}
   */
  #pageRect(pageNumber) {
    const bounds = this.#pageBounds(pageNumber);
    if (!bounds) return;

    const firstRect = bounds.first.getBoundingClientRect();
    const lastRect = bounds.last.getBoundingClientRect();

    return {
      top: Math.min(firstRect.top, lastRect.top),
      bottom: Math.max(firstRect.bottom, lastRect.bottom),
    };
  }

  /**
   * @returns {number[]}
   */
  #visiblePageNumbers() {
    const pages = [];

    for (const page of this.#renderedPageNumbers()) {
      const rect = this.#pageRect(page);
      if (!rect) continue;
      if (rect.bottom > 0 && rect.top < window.innerHeight) pages.push(page);
    }

    return pages;
  }

  /**
   * @returns {number | undefined}
   */
  #dominantVisiblePage() {
    let bestPage;
    let bestHeight = 0;

    for (const page of this.#renderedPageNumbers()) {
      const rect = this.#pageRect(page);
      if (!rect) continue;

      const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      if (visibleHeight > bestHeight) {
        bestPage = page;
        bestHeight = visibleHeight;
      }
    }

    return bestPage;
  }

  #syncUrlFromViewport() {
    if (this.#isMutating) return;

    const page = this.#dominantVisiblePage();
    if (page == null) return;

    const url = new URL(window.location.href);
    const current = url.searchParams.get('page') || '1';
    if (current === String(page)) return;

    url.searchParams.set('page', String(page));
    const hashId = url.hash.replace(/^#/, '');
    if (hashId && !document.getElementById(hashId)) {
      url.hash = '';
    }

    history.replaceState({}, '', url.toString());
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = () => {
    this.#fetchGeneration += 1;
    this.pages.clear();
    this.#detachedPages.clear();
    this.#renderedPages.clear();
    this.#pageFetches.clear();
    this.#warmedPages.clear();
    this.#requireScrollDownBeforePrevious = false;
    this.#previousCooldownUntil = 0;
    this.#previousIntersecting = false;
    this.#nextIntersecting = false;
    this.#scrollDirection = 'none';

    const { grid } = this.refs;
    if (!grid) return;

    const observer = new MutationObserver(() => {
      observer.disconnect();

      if (!this.isConnected) return;

      this.#rebuildRenderedPagesFromDOM();
      this.#snapshotDomPagesToCache();
      this.#observeViewMore();
      this.#prefetchAround();
    });

    observer.observe(grid, {
      attributes: true,
      attributeFilter: ['data-last-page'],
      childList: true,
      subtree: false,
    });

    setTimeout(() => {
      observer.disconnect();
    }, 3000);
  };
}
